import { type FormEvent, useEffect, useMemo, useRef, useState } from 'react'
import { Save } from 'lucide-react'
import { useStudioLocale } from '@/i18n'
import { useStudioStore } from '@/store/studio-store'
import { COOGO_IDE_CHROME_COPY } from '../coogo-ide-chrome-copy'
import { AskCogoSurfaceButton } from '../components/ask-cogo-surface-button'

/**
 * Settings surface — IDE v2 inner shell for the Settings rail item.
 */
export function SettingsSurface() {
  const { locale } = useStudioLocale()
  const chromeCopy = COOGO_IDE_CHROME_COPY[locale]
  const copy = chromeCopy.settingsSurface
  const projectMeta = useStudioStore((s) => s.projectMeta)
  const projectId = useStudioStore((s) => s.currentProjectId)
  const projectSource = useStudioStore((s) => s.projectSource)
  const renameProject = useStudioStore((s) => s.renameProject)
  const updateProjectMeta = useStudioStore((s) => s.updateProjectMeta)
  const [name, setName] = useState(projectMeta?.name ?? '')
  const [description, setDescription] = useState(projectMeta?.description ?? '')
  const [saveState, setSaveState] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle')
  const [saveError, setSaveError] = useState<string | null>(null)
  const lastProjectIdentityRef = useRef(projectMeta?.id ?? projectId ?? null)

  useEffect(() => {
    const nextProjectIdentity = projectMeta?.id ?? projectId ?? null;
    const projectChanged =
      lastProjectIdentityRef.current !== nextProjectIdentity;
    lastProjectIdentityRef.current = nextProjectIdentity;
    setName(projectMeta?.name ?? "");
    setDescription(projectMeta?.description ?? "");
    setSaveError(null);
    setSaveState((current) => {
      if (projectChanged) return "idle";
      return current === "saved" ? "saved" : "idle";
    });
  }, [projectId, projectMeta?.id, projectMeta?.name, projectMeta?.description]);

  const trimmedName = name.trim();
  const normalizedDescription = description.trim();
  const unchanged = useMemo(() => {
    return (
      trimmedName === (projectMeta?.name ?? "").trim() &&
      normalizedDescription === (projectMeta?.description ?? "").trim()
    );
  }, [
    normalizedDescription,
    projectMeta?.description,
    projectMeta?.name,
    trimmedName,
  ]);
  const canSave = Boolean(
    projectId &&
    projectMeta &&
    trimmedName &&
    !unchanged &&
    saveState !== "saving",
  );

  async function handleSave(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!canSave) return;
    setSaveState("saving");
    setSaveError(null);
    try {
      if (trimmedName !== (projectMeta?.name ?? "").trim()) {
        await renameProject(trimmedName);
      }
      if (normalizedDescription !== (projectMeta?.description ?? "").trim()) {
        await updateProjectMeta({ description: normalizedDescription });
      }
      setSaveState('saved')
    } catch {
      setSaveState('error')
      setSaveError(copy.saveFailed)
    }
  }

  return (
    <div className="h-full overflow-auto p-8">
      <div className="mx-auto max-w-3xl">
        <header className="mb-6 flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="mb-2 font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
              § {copy.eyebrow}
            </div>
            <h1 className="text-2xl font-semibold tracking-tight text-foreground">{copy.title}</h1>
            <p className="mt-1 text-sm text-muted-foreground">{copy.summary}</p>
          </div>
          <AskCogoSurfaceButton
            label={copy.reviewSettings}
            context={() =>
              [
                "Studio settings surface: review project name, description, slug, build target, and design system preset.",
                `Project=${projectMeta?.name ?? projectId ?? "none"}`,
                `Target=${projectMeta?.app_target ?? "web"}`,
                `Slug=${projectMeta?.slug ?? "none"}`,
              ].join(" ")
            }
          />
        </header>

        <div className="space-y-6">
          <form onSubmit={handleSave} className="rounded-lg border border-border bg-card">
            <SectionHeader title={copy.general} />
            <div className="space-y-4 px-5 py-4">
              <label className="block">
                <span className="text-xs font-medium text-muted-foreground">{copy.projectName}</span>
                <input
                  value={name}
                  onChange={(event) => {
                    setName(event.target.value);
                    setSaveState("idle");
                    setSaveError(null);
                  }}
                  disabled={
                    !projectId || !projectMeta || saveState === "saving"
                  }
                  className="mt-1 h-9 w-full rounded-md border border-input bg-background px-3 text-sm text-foreground outline-none transition-colors placeholder:text-muted-foreground focus:border-ring focus:ring-2 focus:ring-ring/20 disabled:cursor-not-allowed disabled:opacity-50"
                  placeholder={copy.untitledProject}
                />
              </label>
              <label className="block">
                <span className="text-xs font-medium text-muted-foreground">{copy.description}</span>
                <textarea
                  value={description}
                  onChange={(event) => {
                    setDescription(event.target.value);
                    setSaveState("idle");
                    setSaveError(null);
                  }}
                  disabled={
                    !projectId || !projectMeta || saveState === "saving"
                  }
                  className="mt-1 min-h-20 w-full resize-y rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground outline-none transition-colors placeholder:text-muted-foreground focus:border-ring focus:ring-2 focus:ring-ring/20 disabled:cursor-not-allowed disabled:opacity-50"
                  placeholder={copy.descriptionPlaceholder}
                />
              </label>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <p className="min-h-4 text-xs text-muted-foreground" aria-live="polite">
                  {saveState === 'saved' && copy.saved}
                  {saveState === 'error' && (saveError ?? copy.saveFailed)}
                  {!projectId && copy.openLiveProject}
                  {projectId && !projectMeta && projectSource === 'server' && copy.metadataLoading}
                  {projectId && !projectMeta && projectSource !== 'server' && copy.openLiveProject}
                  {projectId && projectMeta && !trimmedName && copy.projectNameRequired}
                </p>
                <button
                  type="submit"
                  disabled={!canSave}
                  className="inline-flex h-8 items-center gap-2 rounded-md border border-border bg-foreground px-3 text-xs font-medium text-background transition-colors hover:bg-foreground/90 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <Save className="h-3.5 w-3.5" aria-hidden />
                  {saveState === 'saving' ? copy.saving : copy.saveChanges}
                </button>
              </div>
            </div>
            <Row label={copy.slug}>
              <code className="font-mono text-xs text-foreground">{projectMeta?.slug ?? '—'}</code>
            </Row>
            <Row label={copy.projectId}>
              <code className="font-mono text-[10px] text-muted-foreground">{projectId ?? '—'}</code>
            </Row>
          </form>

          <section className="rounded-lg border border-border bg-card">
            <SectionHeader title={copy.buildTarget} />
            <Row label={copy.platform}>
              <span className="font-mono text-xs text-foreground">{projectMeta?.app_target ?? 'web'}</span>
            </Row>
            <Row label={copy.designSystemPreset}>
              <code className="font-mono text-xs text-muted-foreground">
                {projectMeta?.design_system_preset_id ?? copy.noneBound}
              </code>
            </Row>
          </section>

          <section className="rounded-lg border border-destructive/30 bg-destructive/5">
            <SectionHeader title={copy.dangerZone} tone="destructive" />
            <div className="px-5 py-4">
              <p className="text-xs text-muted-foreground">
                {copy.dangerLead} <strong className="text-foreground">{chromeCopy.backToProjects}</strong>
                {copy.dangerTail}
              </p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}

function SectionHeader({
  title,
  tone,
}: {
  title: string;
  tone?: "destructive";
}) {
  return (
    <div
      className={`border-b px-5 py-3 ${
        tone === "destructive"
          ? "border-destructive/30 text-destructive"
          : "border-border text-foreground"
      }`}
    >
      <h2 className="text-sm font-semibold">{title}</h2>
    </div>
  );
}

function Row({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-start justify-between gap-6 border-b border-border px-5 py-3 last:border-0">
      <span className="shrink-0 text-xs font-medium text-muted-foreground">
        {label}
      </span>
      <div className="min-w-0 flex-1 text-right">{children}</div>
    </div>
  );
}
